import React, { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { 
  Settings, 
  LogOut, 
  Home,
  ChevronLeft,
  ChevronRight,
  Plus,
  Minus,
} from "lucide-react";
import { sidebarSections, topNavItems } from "./sidebar/navItems";
import temwiseLogo from "@/assets/temwise-logo.png";
import temwiseIcon from "@/assets/temwise-icon.png";
import defaultAvatar from "@/assets/default-avatar.png";
import { supabase } from "@/integrations/supabase/client";

interface DashboardSidebarProps {
  className?: string;
}

interface UserProfile {
  name: string;
  email: string;
  role: string;
}

const DashboardSidebar = ({ className }: DashboardSidebarProps) => {
  const [collapsed, setCollapsed] = useState(false);
  const [expandedSections, setExpandedSections] = useState<string[]>(["Requests", "Knowledge", "My Firm"]);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const { signOut, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Fetch user profile
  useEffect(() => {
    const fetchProfile = async () => {
      if (!user) return;
      
      const { data, error } = await supabase
        .from("profiles")
        .select("name, email, role")
        .eq("id", user.id)
        .single();
      
      if (data && !error) {
        const isAdmin = data.email === "hamaad_karim@hotmail.com";
        setUserProfile({
          name: data.name || user.email?.split("@")[0] || "User",
          email: data.email || user.email || "",
          role: isAdmin ? "Administrator" : (data.role || "User")
        });
      } else {
        const isAdmin = user.email === "hamaad_karim@hotmail.com";
        setUserProfile({
          name: user.user_metadata?.name || user.email?.split("@")[0] || "User",
          email: user.email || "",
          role: isAdmin ? "Administrator" : "User"
        });
      }
    };

    fetchProfile();
  }, [user]);

  const handleSignOut = async () => {
    await signOut();
    navigate("/signin");
  };

  const toggleSection = (sectionName: string) => {
    setExpandedSections(prev => 
      prev.includes(sectionName) 
        ? prev.filter(s => s !== sectionName)
        : [...prev, sectionName]
    );
  };

  const isActive = (path: string) => location.pathname === path;

  const homeItem = topNavItems.find(item => item.name === "Home");

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map(n => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div
      className={cn(
        "relative flex flex-col h-screen transition-all duration-300 ease-in-out overflow-hidden",
        collapsed ? "w-[72px]" : "w-64",
        className
      )}
      style={{ backgroundColor: "#040435" }}
    >
      {/* Logo - centered when expanded */}
      <div className={cn(
        "flex items-center h-20 transition-all duration-300 shrink-0 justify-center",
        collapsed ? "px-2" : "px-6"
      )}>
        {collapsed ? (
          <img src={temwiseIcon} alt="Temwise" className="h-10 w-10" />
        ) : (
          <img src={temwiseLogo} alt="Temwise" className="h-8" />
        )}
      </div>
      
      {/* Navigation with custom scrollbar */}
      <nav className="flex-1 overflow-y-auto overflow-x-hidden py-4 px-2 sidebar-scroll">
        {/* Home Button */}
        {homeItem && (
          <Link to={homeItem.path}>
            <div
              className={cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-lg mb-4 transition-all duration-200",
                collapsed ? "justify-center" : "",
                isActive(homeItem.path)
                  ? "bg-[#3B82F6] text-white"
                  : "text-white/80 hover:bg-white/10"
              )}
            >
              <Home className="h-4 w-4 shrink-0" />
              {!collapsed && (
                <span className="text-sm font-medium whitespace-nowrap">Home</span>
              )}
            </div>
          </Link>
        )}

        {/* Sections */}
        {sidebarSections.map((section) => {
          const isExpanded = expandedSections.includes(section.name);

          return (
            <div key={section.name}>
              {/* Divider before each section */}
              <div className="px-3 my-3">
                <div className="h-px bg-white/10" />
              </div>
              
              <div className="mb-3">
              {/* Section Header - hidden when collapsed */}
              {!collapsed && (
                <button
                  onClick={() => toggleSection(section.name)}
                  className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold uppercase tracking-wider transition-colors hover:text-white/80"
                  style={{ color: "rgba(255,255,255,0.5)" }}
                >
                  <span className="whitespace-nowrap">{section.name}</span>
                  {isExpanded ? (
                    <Minus className="h-3 w-3" />
                  ) : (
                    <Plus className="h-3 w-3" />
                  )}
                </button>
              )}

              {/* Section Items */}
              <div className={cn(
                "space-y-0.5 mt-1",
                !isExpanded && !collapsed && "hidden"
              )}>
                {section.items.map((item) => {
                  const ItemIcon = item.icon;
                  const isItemActive = isActive(item.path);
                  
                  return (
                    <Link key={item.path} to={item.path}>
                      <div
                        className={cn(
                          "flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200",
                          collapsed ? "justify-center" : "",
                          isItemActive
                            ? "bg-[#3B82F6] text-white"
                            : "text-white/70 hover:bg-white/10 hover:text-white"
                        )}
                      >
                        <ItemIcon className="h-4 w-4 shrink-0" />
                        {!collapsed && (
                          <span className="text-sm whitespace-nowrap">{item.name}</span>
                        )}
                      </div>
                    </Link>
                  );
                })}
              </div>
              </div>
            </div>
          );
        })}
      </nav>
      
      {/* Bottom Section */}
      <div className="py-4 px-2 shrink-0">
        {/* Settings */}
        <button
          onClick={() => navigate('/settings')}
          className={cn(
            "w-full flex items-center gap-3 py-2 rounded-lg transition-all duration-200 text-white/70 hover:bg-white/10 hover:text-white",
            collapsed ? "justify-center pr-1" : "px-3"
          )}
        >
          <Settings className="h-4 w-4 shrink-0" />
          {!collapsed && (
            <span className="text-sm whitespace-nowrap">Settings</span>
          )}
        </button>
        
        {/* Sign Out */}
        <button
          onClick={handleSignOut}
          className={cn(
            "w-full flex items-center gap-3 py-2 rounded-lg transition-all duration-200 text-white/70 hover:bg-white/10 hover:text-white",
            collapsed ? "justify-center pr-1" : "px-3"
          )}
        >
          <LogOut className="h-4 w-4 shrink-0" />
          {!collapsed && (
            <span className="text-sm whitespace-nowrap">Sign Out</span>
          )}
        </button>

        {/* Avatar with User Info */}
        <div className={cn(
          "flex items-center gap-3 mt-4 px-2",
          collapsed ? "justify-center" : ""
        )}>
          <Avatar className="h-10 w-10 ring-2 ring-green-500 ring-offset-2 ring-offset-[#040435] shrink-0">
            <AvatarImage src={defaultAvatar} alt={userProfile?.name || "User"} />
            <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-600 text-white text-sm font-medium">
              {userProfile ? getInitials(userProfile.name) : "U"}
            </AvatarFallback>
          </Avatar>
          {!collapsed && userProfile && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{userProfile.name}</p>
              <p className="text-xs text-white/50 truncate">{userProfile.role}</p>
            </div>
          )}
        </div>

        {/* Collapse Toggle Button */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className={cn(
            "w-full flex items-center gap-3 px-3 py-2 mt-4 rounded-lg transition-all duration-200 text-white/70 hover:bg-white/10 hover:text-white",
            collapsed ? "justify-center" : ""
          )}
        >
          {collapsed ? (
            <ChevronRight className="h-5 w-5 shrink-0" />
          ) : (
            <>
              <ChevronLeft className="h-5 w-5 shrink-0" />
              <span className="text-sm whitespace-nowrap">Collapse</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default DashboardSidebar;